import React, { useMemo } from "react";
import { formatDuration } from "../../../utils/formatDuration";
import { colors } from "../../../styles/theme";
import { CircularProgressChart, ChartSegment } from "./CircularProgressChart";
import { ChartLegend, LegendItem } from "./ChartLegend";
import { calculateTotal, addPercentages } from "../utils/calculations";

export interface AgingData {
  distribution: {
    lessThan1d: number;
    oneTo3d: number;
    threeTo7d: number;
    sevenTo14d: number;
    moreThan14d: number;
  };
}

interface ApplicationAgingChartProps {
  avgTimeSinceLastActivity: number;
  aging: AgingData;
  onLegendClick?: (segmentKey: string) => void;
}

export const ApplicationAgingChart: React.FC<ApplicationAgingChartProps> = ({
  avgTimeSinceLastActivity,
  aging,
  onLegendClick,
}) => {
  // Convert distribution object to array, calculate total once, then add percentages
  const { distributionWithPercentages, total } = useMemo(() => {
    const distributionArray = [
      { key: "lessThan1d", count: aging.distribution.lessThan1d },
      { key: "oneTo3d", count: aging.distribution.oneTo3d },
      { key: "threeTo7d", count: aging.distribution.threeTo7d },
      { key: "sevenTo14d", count: aging.distribution.sevenTo14d },
      { key: "moreThan14d", count: aging.distribution.moreThan14d },
    ];
    const total = calculateTotal(distributionArray, "count");
    const withPercentages = addPercentages(distributionArray, "count", total);
    return { distributionWithPercentages: withPercentages, total };
  }, [aging.distribution]);

  // Color palette for the segments
  const segmentColors = {
    lessThan1d: "#b8e986", // Lighter green (best - recent activity)
    oneTo3d: colors.brandPrimary, // Teal
    threeTo7d: "#f5a8b0", // Lighter coral
    sevenTo14d: colors.brandOrange, // Orange
    moreThan14d: colors.brandCoral, // Coral (worst - stale)
  };

  const titleMap: Record<string, string> = {
    lessThan1d: "Recently Active (<24 hours)",
    oneTo3d: "Active 1-3 Days Ago",
    threeTo7d: "Active 3-7 Days Ago",
    sevenTo14d: "Active 1-2 Weeks Ago",
    moreThan14d: "No Activity in 2 Weeks",
  };

  // Build segments for CircularProgressChart
  const segments: ChartSegment[] = distributionWithPercentages.map((item) => ({
    key: item.key,
    percentage: item.percentage,
    color: segmentColors[item.key as keyof typeof segmentColors],
    label: titleMap[item.key],
    count: item.count,
  }));

  // Build legend items
  const legendItems: LegendItem[] = distributionWithPercentages.map((item) => ({
    label: titleMap[item.key],
    color: segmentColors[item.key as keyof typeof segmentColors],
    percentage: item.percentage.toFixed(1),
  }));

  // Handler for legend item clicks
  const handleLegendClick = (itemIndex: number) => {
    if (onLegendClick) {
      const segmentKey = distributionWithPercentages[itemIndex].key;
      onLegendClick(`activity_${segmentKey}`);
    }
  };

  // Center content for the circular chart
  const centerContent = (
    <>
      <div
        style={{
          fontSize: "13px",
          color: "#8c8c8c",
          marginBottom: "6px",
        }}
      >
        Avg Time Since Last Activity
      </div>
      <div
        style={{
          fontSize: "32px",
          fontWeight: "bold",
          color: "#262626",
          lineHeight: 1.2,
        }}
      >
        {formatDuration(avgTimeSinceLastActivity)}
      </div>
      <div
        style={{
          fontSize: "12px",
          color: "#8c8c8c",
          marginTop: "8px",
        }}
      >
        {total.toLocaleString()} pending apps
      </div>
    </>
  );

  return (
    <div
      style={{
        display: "flex",
        gap: "32px",
        alignItems: "center",
        padding: "20px 0",
      }}
    >
      {/* Left side: Circular Progress */}
      <div style={{ flex: "0 0 auto" }}>
        <CircularProgressChart
          segments={segments}
          centerContent={centerContent}
        />
      </div>

      {/* Right side: Legend */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <ChartLegend items={legendItems} onClick={handleLegendClick} />
      </div>
    </div>
  );
};
